import {useEffect, useState} from 'react';
import {useParams, Link, useLocation, useHistory} from 'react-router-dom';
import {
  Button,
  Card,
  Col,
  Empty,
  Input,
  InputNumber,
  Row,
  Select,
  Spin,
  Tag,
  Typography,
  DatePicker,
  notification,
} from 'antd';
import {
  PlusOutlined,
  CalendarOutlined,
  DollarOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import {StudentsService} from './StudentsService';
import {SchoolService} from '../Schools/SchoolService';
import {EnrollmentsService} from '../Enrollments/EnrollmentsService';
import type {NewEnrollment} from '@PikaDoApp/npm-pikado-logic/dist/modules/enrollment/graphql/enrollment.interface';

const studentsService = new StudentsService();
const schoolService = new SchoolService();
const enrollmentsService = new EnrollmentsService();

const enrollmentStatusColor: Record<string, string> = {
  ACTIVE: 'green',
  EXPIRED: 'red',
};

const placementStatusColor: Record<string, string> = {
  TODO: 'default',
  DOING: 'processing',
  DONE: 'success',
};

export const StudentEdit = () => {
  const [student, setStudent] = useState<any>(undefined);
  const [enrollments, setEnrollments] = useState<NewEnrollment[]>([]);
  const [isEnrollmentsLoading, setIsEnrollmentsLoading] =
    useState<boolean>(true);
  const [isError, setIsError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [openClassSchoolDropDown, setOpenClassSchoolDropDown] =
    useState<boolean>(false);
  const [classes, setClasses] = useState<any[]>([]);
  const params: {studentId: string} = useParams();
  const history = useHistory();

  const location: {
    state: any;
    hash: any;
    search: any;
    pathname: any;
  } = useLocation();
  const {Title, Text} = Typography;

  useEffect(() => {
    studentsService
      .getStudentById(params.studentId)
      .then(resp => {
        setIsLoading(false);
        setStudent(resp);
      })
      .catch(() => {
        setIsLoading(false);
        setIsError(true);
      });

    setIsEnrollmentsLoading(true);
    enrollmentsService
      .getEnrollmentsByStudentId(params.studentId)
      .then(resp => {
        setEnrollments(resp);
        setIsEnrollmentsLoading(false);
      })
      .catch(() => {
        setIsEnrollmentsLoading(false);
      });
  }, [location.pathname]);

  const formatDate = (dateStr?: string | null) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  const onChange = (prop: any, value: any) => {
    setStudent((prev: any) => ({...prev, [prop]: value}));
  };

  const onSave = async () => {
    if (!student?.id) {
      notification.error({message: 'Student not loaded yet'});
      return;
    }

    try {
      await studentsService.updateStudent(student);
      notification.success({message: 'Student updated successfully'});
      history.push('/students/' + student?.id);
    } catch (error: any) {
      notification.error({
        message: 'Student update failed',
        description: error?.message || 'Please try again.',
      });
    }
  };

  const getClassSchoolsOfSchool = async () => {
    const schoolId = student?.schoolId ?? student?.school?.id;
    if (!schoolId) {
      notification.warning({message: 'No school ID found for this student'});
      return;
    }

    schoolService?.getClassesBySchoolId(schoolId).then(e => {
      setClasses(e.items ?? []);
    });
  };

  return (
    <div style={{padding: 10}}>
      <Title style={{textAlign: 'center'}}>Student</Title>

      {!isError && !isLoading && (
        <>
          <Row>
            <Col span={12}>
              <Text style={{marginInline: 20, fontWeight: 'bold'}}>
                Student ID: {student?.id}
              </Text>
            </Col>
            <Col span={12}>
              <Text
                style={{fontSize: 16, fontWeight: 'bold', marginInline: 20}}>
                User ID: {student?.userId ?? student?.user?.id ?? '-'}
              </Text>
            </Col>
          </Row>

          <Row>
            <Col span={12}>
              <div style={{margin: 20}}>
                <Text style={{display: 'block'}} strong={true}>
                  Student Name
                </Text>
                <Input
                  value={student?.name ?? ''}
                  onChange={event => onChange('name', event.target.value)}
                />
              </div>
            </Col>
            <Col span={12}>
              <div style={{margin: 20}}>
                <Text style={{display: 'block'}} strong={true}>
                  Gender
                </Text>
                <Select
                  style={{width: '100%'}}
                  value={student?.gender}
                  onChange={value => onChange('gender', value)}>
                  <Select.Option value="FEMALE">Female</Select.Option>
                  <Select.Option value="MALE">Male</Select.Option>
                </Select>
              </div>
            </Col>
          </Row>

          <Row>
            <Col span={12}>
              <div style={{margin: 20}}>
                <Text style={{display: 'block'}} strong={true}>
                  Age
                </Text>
                <InputNumber
                  style={{width: '100%'}}
                  min={1}
                  value={student?.age}
                  onChange={value => onChange('age', value)}
                />
              </div>
            </Col>
            <Col span={12}>
              <div style={{margin: 20}}>
                <Text style={{display: 'block'}} strong={true}>
                  Birth Date
                </Text>
                <DatePicker
                  style={{width: '100%'}}
                  value={student?.birthDate ? moment(student.birthDate) : null}
                  onChange={value =>
                    onChange(
                      'birthDate',
                      value ? value.format('YYYY-MM-DD') : undefined,
                    )
                  }
                />
              </div>
            </Col>
          </Row>

          <Row>
            <Col span={12}>
              <div style={{margin: 20}}>
                <Text style={{display: 'block'}} strong={true}>
                  School ID
                </Text>
                <Input
                  value={student?.schoolId ?? student?.school?.id ?? ''}
                  onChange={event => onChange('schoolId', event.target.value)}
                />
              </div>
            </Col>
            <Col span={12}>
              <div style={{margin: 20}}>
                <Text style={{display: 'block'}} strong={true}>
                  Class ID
                </Text>
                <Input
                  value={
                    student?.classSchoolId ??
                    student?.classschoolId ??
                    student?.classschool?.id ??
                    ''
                  }
                  onChange={event =>
                    onChange('classSchoolId', event.target.value)
                  }
                />
                <Button
                  style={{marginTop: 8}}
                  onClick={async () => {
                    await getClassSchoolsOfSchool();
                    setOpenClassSchoolDropDown(true);
                  }}>
                  Change class from school classes
                </Button>
                {openClassSchoolDropDown && (
                  <Select
                    showSearch
                    style={{width: '100%', marginTop: 8}}
                    placeholder="Select a class"
                    optionFilterProp="children"
                    onChange={(classschoolId: string) => {
                      onChange('classSchoolId', classschoolId);
                    }}>
                    {classes?.map(schoolClass => (
                      <Select.Option
                        key={schoolClass.id}
                        value={schoolClass.id}>
                        {schoolClass.name}
                      </Select.Option>
                    ))}
                  </Select>
                )}
              </div>
            </Col>
          </Row>

          <Row>
            <Col span={12}>
              <div style={{margin: 20}}>
                <Text style={{display: 'block'}} strong={true}>
                  Level ID
                </Text>
                <Input
                  value={student?.levelId ?? student?.level?.id ?? ''}
                  onChange={event => onChange('levelId', event.target.value)}
                />
              </div>
            </Col>
            <Col span={12}>
              <div style={{margin: 20}}>
                <Text style={{display: 'block'}} strong={true}>
                  Grade ID
                </Text>
                <Input
                  value={student?.gradeId ?? student?.grade?.id ?? ''}
                  onChange={event => onChange('gradeId', event.target.value)}
                />
              </div>
            </Col>
          </Row>

          <Row>
            <Col span={12}>
              <div style={{margin: 20}}>
                <Text style={{display: 'block'}} strong={true}>
                  User Email
                </Text>
                <Text>{student?.user?.email ?? '-'}</Text>
              </div>
            </Col>
            <Col span={12}>
              <div style={{margin: 20}}>
                <Text style={{display: 'block'}} strong={true}>
                  Whatsapp Number
                </Text>
                <Text>{student?.user?.whatsappNumber ?? '-'}</Text>
              </div>
            </Col>
          </Row>

          <Row>
            <Col span={24}>
              <div style={{margin: 20}}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 12,
                  }}>
                  <Text strong={true} style={{fontSize: 16}}>
                    Student Enrollments ({enrollments.length})
                  </Text>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() =>
                      history.push({
                        pathname: '/enrollments/create',
                        state: {
                          studentId: params.studentId,
                          studentName: student?.name,
                        },
                      })
                    }>
                    Create New Enrollment
                  </Button>
                </div>
                {isEnrollmentsLoading ? (
                  <Spin
                    size="small"
                    style={{display: 'block', textAlign: 'center', padding: 20}}
                  />
                ) : enrollments.length === 0 ? (
                  <Empty
                    description="No enrollments found"
                    style={{padding: 20}}
                  />
                ) : (
                  enrollments.map(enrollment => (
                    <Card
                      key={enrollment._id}
                      size="small"
                      style={{marginBottom: 8}}
                      hoverable
                      onClick={() =>
                        history.push('/enrollments/' + enrollment._id)
                      }>
                      <Row gutter={16} align="middle">
                        <Col flex="auto">
                          <Link
                            to={'/enrollments/' + enrollment._id}
                            style={{fontWeight: 600, fontSize: 14}}
                            onClick={e => e.stopPropagation()}>
                            {enrollment._id}
                          </Link>
                        </Col>
                        <Col>
                          <Tag
                            color={
                              enrollmentStatusColor[
                                enrollment.enrollmentStatus
                              ] || 'default'
                            }>
                            {enrollment.enrollmentStatus}
                          </Tag>
                        </Col>
                        <Col>
                          <Tag
                            color={
                              placementStatusColor[
                                enrollment.placementStatus
                              ] || 'default'
                            }>
                            Placement: {enrollment.placementStatus}
                          </Tag>
                        </Col>
                      </Row>
                      <Row gutter={16} style={{marginTop: 8}}>
                        <Col span={6}>
                          <Text type="secondary" style={{fontSize: 12}}>
                            <CalendarOutlined /> Start
                          </Text>
                          <br />
                          <Text style={{fontSize: 13}}>
                            {formatDate(enrollment.startDate)}
                          </Text>
                        </Col>
                        <Col span={6}>
                          <Text type="secondary" style={{fontSize: 12}}>
                            <CalendarOutlined /> End
                          </Text>
                          <br />
                          <Text style={{fontSize: 13}}>
                            {formatDate(enrollment.endDate)}
                          </Text>
                        </Col>
                        <Col span={6}>
                          <Text type="secondary" style={{fontSize: 12}}>
                            <DollarOutlined /> Price
                          </Text>
                          <br />
                          <Text style={{fontSize: 13}}>
                            {enrollment.finalPrice
                              ? `${enrollment.finalPrice.price ?? '-'} ${enrollment.finalPrice.currency}`
                              : '-'}
                          </Text>
                        </Col>
                        <Col span={6}>
                          <Text type="secondary" style={{fontSize: 12}}>
                            Payment
                          </Text>
                          <br />
                          <Text style={{fontSize: 13}}>
                            {enrollment.paymentMethod ?? '-'}
                            {enrollment.isFree && (
                              <Tag color="blue" style={{marginLeft: 4}}>
                                Free
                              </Tag>
                            )}
                          </Text>
                        </Col>
                      </Row>
                    </Card>
                  ))
                )}
              </div>
            </Col>
          </Row>

          <Button
            size={'large'}
            style={{float: 'right'}}
            onClick={() => onSave()}>
            Save
          </Button>
        </>
      )}
      {isLoading && (
        <Spin size="large" style={{margin: 'auto', width: '100%'}} />
      )}
      {isError && <Title>Student fetching error</Title>}
    </div>
  );
};
